/** @jsxImportSource hono/jsx/dom */
import type { FC } from 'hono/jsx'
import { useCallback, useEffect, useRef, useState } from 'hono/jsx'
import { normalizePronunciation, normalizeWord } from '../lib/normalize'
import { ApiError, register } from './api'
import { navigate } from './router'
import { getVoterId, setVoted } from './storage'
import { TurnstileField } from './turnstile'

export const RegisterWidget: FC<{ siteKey: string; initialWord: string }> = ({
  siteKey,
  initialWord,
}) => {
  const wordRef = useRef<HTMLInputElement | null>(null)
  const pronunciationRef = useRef<HTMLInputElement | null>(null)
  const turnstileRef = useRef<HTMLDivElement | null>(null)
  const fieldRef = useRef<TurnstileField | null>(null)

  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  // 正規化の先行表示 (UXのためだけのもの。サーバー側の正規化が正)
  const [preview, setPreview] = useState<{ word: string; pronunciation: string } | null>(null)

  useEffect(() => {
    const field = new TurnstileField()
    fieldRef.current = field
    if (turnstileRef.current) {
      void field.mount(turnstileRef.current, siteKey).catch(() => {
        setError('認証ウィジェットを読み込めませんでした')
      })
    }
    return () => field.destroy()
  }, [])

  const updatePreview = useCallback(() => {
    const word = normalizeWord(wordRef.current?.value ?? '')
    const pronunciation = normalizePronunciation(pronunciationRef.current?.value ?? '')
    setPreview(word === '' && pronunciation === '' ? null : { word, pronunciation })
  }, [])

  const onSubmit = useCallback(
    async (event: Event) => {
      event.preventDefault()
      const field = fieldRef.current
      if (busy || !field) return
      const word = wordRef.current?.value ?? ''
      const pronunciation = pronunciationRef.current?.value ?? ''
      if (word.trim() === '' || pronunciation.trim() === '') return

      setBusy(true)
      setError(null)
      try {
        const token = await field.getToken()
        const result = await register({
          word,
          pronunciation,
          voterId: getVoterId(),
          turnstileToken: token,
        })
        field.reset()
        setVoted(result.word, result.voted)
        if (pronunciationRef.current) pronunciationRef.current.value = ''
        if (wordRef.current) wordRef.current.value = result.word
        setPreview(null)
        // 画面遷移なしで単語画面に差し替え、URLを /:word に書き換える
        navigate(result.word, {
          state: { word: result.word, pronunciations: result.pronunciations, registered: true },
        })
      } catch (err) {
        fieldRef.current?.reset()
        setError(err instanceof ApiError ? err.message : '通信に失敗しました')
      } finally {
        setBusy(false)
      }
    },
    [busy]
  )

  return (
    <div>
      <form class="stack" onSubmit={onSubmit}>
        <label class="field">
          <span class="field-label">単語</span>
          <input
            ref={wordRef}
            type="text"
            name="word"
            required
            maxlength={100}
            autocomplete="off"
            value={initialWord}
            placeholder="git"
            onInput={updatePreview}
          />
        </label>
        <label class="field">
          <span class="field-label">読み方</span>
          <input
            ref={pronunciationRef}
            type="text"
            name="pronunciation"
            required
            maxlength={100}
            autocomplete="off"
            placeholder="ギット"
            onInput={updatePreview}
          />
        </label>
        <div class="turnstile-holder" ref={turnstileRef} />
        <button type="submit" disabled={busy}>
          {busy ? '送信中…' : '登録する'}
        </button>
      </form>

      {preview ? (
        <p class="preview">
          登録される内容: <b>{preview.word || '—'}</b> / <b>{preview.pronunciation || '—'}</b>
        </p>
      ) : null}

      {error ? (
        <p class="error-text" role="status">
          {error}
        </p>
      ) : null}
    </div>
  )
}
